import React, { useEffect, useState } from "react";
import { fetchTradingHistory, type TradingHistoryItem } from "../services/tradingHistoryService";

interface TradingHistoryPanelProps {
  tokenId?: string | number;
  defaultTokenSymbol?: string;
  refreshSignal?: number;
}

const shorten = (value: string) => {
  if (!value) return "--";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const formatNumber = (value: number | string | null | undefined, digits = 3) => {
  if (value === null || value === undefined) return "--";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "--";
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
  return formatter.format(numeric);
};

const formatPrice = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) return "--";
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "--";
  return numeric < 0.01 ? numeric.toExponential(2) : numeric.toFixed(6);
};

const formatTimeAgo = (input: string | number | Date) => {
  const created = new Date(input).getTime();
  if (Number.isNaN(created)) return "--";
  const diff = Date.now() - created;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const TradingHistoryPanel: React.FC<TradingHistoryPanelProps> = ({
  tokenId,
  defaultTokenSymbol,
  refreshSignal = 0,
}) => {
  const [rows, setRows] = useState<TradingHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await fetchTradingHistory({
          token: tokenId ?? null,
          order: "desc",
          limit: 50,
        });
        setRows(payload.transactions);
      } catch (err: any) {
        console.error("Failed to load trading history", err);
        setError(err?.message ?? "Failed to load trading history");
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [tokenId, refreshSignal]);

  return (
    <div className="th-panel-simple">
      <h3 className="th-simple-title">Recent Trades</h3>
      {loading ? (
        <div className="th-simple-state">Loading trading history…</div>
      ) : error ? (
        <div className="th-simple-state th-simple-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="th-simple-state">No trades yet. Be the first to trade!</div>
      ) : (
        <ul className="th-simple-list">
          {rows.map((trade) => {
            const shortWallet = shorten(trade.userWallet);
            const shortHash = trade.transactionHash
              ? `${trade.transactionHash.slice(0, 10)}...${trade.transactionHash.slice(-6)}`
              : "--";
            const suiAmount = formatNumber(trade.suiAmount, 4);
            const tokenAmount = formatNumber(trade.tokenAmount, 2);
            const resolvedToken = trade.tokenSymbol ?? defaultTokenSymbol ?? "";
            const price = formatPrice(trade.pricePerToken);

            return (
              <li
                key={`${trade.id}-${trade.transactionHash ?? trade.createdAt}`}
                className={`th-simple-row ${trade.transactionType}`}
              >
                <div className="th-simple-account" title={trade.userWallet}>
                  <span className="th-simple-avatar">{(trade.username ?? shortWallet).slice(0, 2).toUpperCase()}</span>
                  <div className="th-simple-account-meta">
                    <span className="th-simple-name">{trade.username ?? shortWallet}</span>
                    <span className="th-simple-wallet">{shortWallet}</span>
                  </div>
                </div>
                <span className={`th-simple-type ${trade.transactionType}`}>{trade.transactionType.toUpperCase()}</span>
                <span className="th-simple-sui">{suiAmount !== "--" ? `${suiAmount} SUI` : "--"}</span>
                <span className="th-simple-token">
                  {tokenAmount !== "--" ? `${tokenAmount} ${resolvedToken}` : "--"}
                </span>
                <span className="th-simple-price">{price !== "--" ? `${price} SUI` : "--"}</span>
                <span className="th-simple-time">{formatTimeAgo(trade.createdAt)}</span>
                <a
                  className="th-simple-hash"
                  href={trade.transactionHash ? `https://suiscan.xyz/testnet/tx/${trade.transactionHash}` : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={trade.transactionHash ?? "Pending"}
                >
                  {shortHash}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TradingHistoryPanel;
